import { capitalize } from 'inflected';
import { useCallback, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { MdOutlineFileDownload } from 'react-icons/md';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { BPMTable, Header } from '../../Components/StickyTable';
import { Button } from '../../Components/Button';
import { Appearance, Size } from '../../Components/designConstants';
import { Spinner } from '../../Components/Spinner';
import {
  AMAZON,
  AmazonTab,
  BatchJob,
  downloadCsvBlob,
  downloadJson,
  flattenAmazonProduct,
  flattenObject,
  getBatchBannerCounts,
  isAmazonProductBatch,
  Platform,
  ScraperTab
} from './CommerceScraperUtils';
import { HighlightedJson, ProductPreview, ReviewCard } from './ProductPreview';
import { SearchResults } from './SearchResults';

// ── Batch Progress ────────────────────────────────────────────────────

interface BatchProgressProps {
  job: BatchJob;
}

export const BatchProgress = ({ job }: BatchProgressProps) => {
  const pct = job.total > 0 ? Math.round((job.completed / job.total) * 100) : 0;

  return (
    <div className='batch-progress' data-testid='batch-progress'>
      <div className='batch-progress-header'>
        <span className='batch-progress-label'>
          Batch Progress: {job.completed} / {job.total}
        </span>
        <span className='batch-progress-pct'>{pct}%</span>
      </div>
      <div className='batch-progress-bar'>
        <div className='batch-progress-fill' style={{ width: `${pct}%` }} />
      </div>
      <div className='batch-progress-items'>
        {job.items.map((item, i) => (
          <span
            key={i}
            className={`batch-item-dot batch-item-${item.status}`}
            title={`${item.label}: ${item.status}${item.error ? ` — ${item.error}` : ''}`}
          />
        ))}
      </div>
    </div>
  );
};

// DSH-32549: BatchProgress (including its per-item failed dots) only rendered while
// `job.status === 'running'` — the moment a batch completed, any failed items became invisible,
// so a user exporting the results had no way to know rows were missing short of counting them.
// This renders in its place once the job is done, if anything failed, redirected, or came back
// with an incomplete scrape.
interface BatchFailureBannerProps {
  job: BatchJob;
  platform: Platform;
  tab: ScraperTab;
}

const BatchFailureBanner = ({ job, platform, tab }: BatchFailureBannerProps) => {
  const noun = platform === AMAZON ? 'ASIN' : 'product ID';
  const { excludedCount, includedButBadCount, redirectedCount } = getBatchBannerCounts(
    job,
    platform,
    tab
  );

  // DSH-32549 review feedback (I6): a message array + filter/map instead of three copy-pasted
  // conditional blocks that only differed in count and trailing sentence. "Excluded" and
  // "included but bad" are deliberately different claims (see getBatchBannerCounts) — an
  // excluded row genuinely isn't in the results/export, an included-but-bad one is, just with an
  // incomplete scrape — so they keep separate wording rather than being merged.
  const messages = [
    {
      count: excludedCount,
      text:
        `${conditionallyPluralizeWithCount(excludedCount, noun)} of ${job.total} failed to fetch, ` +
        `even after a retry, and ${excludedCount === 1 ? "isn't" : "aren't"} included in the ` +
        `results or export below.`
    },
    {
      count: includedButBadCount,
      text:
        `${conditionallyPluralizeWithCount(includedButBadCount, noun)} of ${job.total} came back ` +
        `with an incomplete scrape — included in the results and export below, but missing ` +
        `pricing, rating, or other core data.`
    },
    {
      count: redirectedCount,
      text:
        `${conditionallyPluralizeWithCount(redirectedCount, noun)} of ${job.total} ` +
        `${redirectedCount === 1 ? 'has' : 'have'} no independent Amazon listing of ` +
        `${redirectedCount === 1 ? 'its' : 'their'} own — Amazon redirected ` +
        `${redirectedCount === 1 ? 'it' : 'them'} to a related product, shown below using that ` +
        `product's data instead.`
    }
  ].filter(m => m.count > 0);

  if (messages.length === 0) {
    return null;
  }
  return (
    <div className='scraper-batch-warning' data-testid='scraper-batch-warning'>
      {messages.map((m, i) => (
        <div key={i}>{m.text}</div>
      ))}
    </div>
  );
};

// ── BPMTable fallback for offers/category/generic ─────────────────────

const FallbackTable = ({ data }: { data: unknown }) => {
  const [subTab, setSubTab] = useState<'table' | 'json'>('table');

  const { headers, rows } = useMemo(() => {
    if (!data) {
      return { headers: [] as Header[], rows: [] as Record<string, string>[] };
    }
    const arr = Array.isArray(data) ? data : [data];
    const flatRows = arr.map(item =>
      typeof item === 'object' && item
        ? flattenObject(item as Record<string, unknown>)
        : { value: String(item) }
    );
    const colSet = new Set<string>();
    for (const row of flatRows) {
      for (const key of Object.keys(row)) {
        colSet.add(key);
      }
    }
    const hdrs = Array.from(colSet).map(col => ({
      label: col,
      name: col,
      flex: 1,
      minFlexWidth: 120
    }));
    return { headers: hdrs, rows: flatRows };
  }, [data]);

  return (
    <div>
      <Nav
        bsPrefix='result-tabs'
        activeKey={subTab}
        onSelect={key => key && setSubTab(key as 'table' | 'json')}
      >
        <Nav.Item>
          <Nav.Link bsPrefix='result-tab' eventKey='table'>
            Results ({rows.length})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link bsPrefix='result-tab' eventKey='json'>
            Raw JSON
          </Nav.Link>
        </Nav.Item>
      </Nav>
      {subTab === 'json' && <HighlightedJson data={data} />}
      {subTab === 'table' && rows.length > 0 && (
        <div className='scraper-bpm-table-wrapper'>
          <BPMTable headers={headers} data={rows} filterBar alternateColors />
        </div>
      )}
      {subTab === 'table' && rows.length === 0 && (
        <div className='scraper-no-results'>No results found.</div>
      )}
    </div>
  );
};

// ── Reviews List ──────────────────────────────────────────────────────

const ReviewsList = ({ data }: { data: unknown }) => {
  const [subTab, setSubTab] = useState<'reviews' | 'json'>('reviews');
  const reviews = useMemo(() => {
    if (Array.isArray(data)) {
      return data as Record<string, unknown>[];
    }
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      if (Array.isArray(obj.reviews)) {
        return obj.reviews as Record<string, unknown>[];
      }
    }
    return [];
  }, [data]);

  return (
    <div>
      <Nav
        bsPrefix='result-tabs'
        activeKey={subTab}
        onSelect={key => key && setSubTab(key as 'reviews' | 'json')}
      >
        <Nav.Item>
          <Nav.Link bsPrefix='result-tab' eventKey='reviews'>
            Reviews ({reviews.length})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link bsPrefix='result-tab' eventKey='json'>
            Raw JSON
          </Nav.Link>
        </Nav.Item>
      </Nav>
      {subTab === 'json' && <HighlightedJson data={data} />}
      {subTab === 'reviews' && reviews.length > 0 && (
        <div className='review-list'>
          {reviews.map((r, i) => (
            <ReviewCard key={i} review={r} />
          ))}
        </div>
      )}
      {subTab === 'reviews' && reviews.length === 0 && (
        <div className='scraper-no-results'>No reviews found.</div>
      )}
    </div>
  );
};

// ── Rich Content Dispatcher ───────────────────────────────────────────

interface RichContentProps {
  platform: Platform;
  tab: ScraperTab;
  data: unknown;
}

const RichContent = ({ platform, tab, data }: RichContentProps) => {
  if (tab === 'product' && data && typeof data === 'object' && !Array.isArray(data)) {
    return <ProductPreview platform={platform} product={data as Record<string, unknown>} />;
  }
  if (tab === 'search' && data && typeof data === 'object' && !Array.isArray(data)) {
    return <SearchResults data={data as Record<string, unknown>} />;
  }
  if (tab === 'reviews') {
    return <ReviewsList data={data} />;
  }
  // category and batch fallback
  return <FallbackTable data={data} />;
};

// ── Main Results Panel ────────────────────────────────────────────────

interface ScraperResultsProps {
  loading: boolean;
  data: unknown | null;
  batchJob: BatchJob | null;
  error: string | null;
  platform: Platform;
  tab: ScraperTab;
}

export const ScraperResults = ({
  loading,
  data,
  batchJob,
  error,
  platform,
  tab
}: ScraperResultsProps) => {
  const handleDownloadJson = useCallback(() => {
    if (data) {
      downloadJson(data, 'scraper-results.json');
    }
  }, [data]);

  const handleDownloadCsv = useCallback(() => {
    if (!data) {
      return;
    }
    const normalizeRow = (item: unknown): Record<string, unknown> => {
      if (!item || typeof item !== 'object') return { value: String(item) };
      if (isAmazonProductBatch(platform, tab)) {
        return flattenAmazonProduct(item as Record<string, unknown>);
      }
      return flattenObject(item as Record<string, unknown>);
    };
    const extractSearchRows = (d: unknown): Record<string, unknown>[] => {
      if (!d || typeof d !== 'object' || Array.isArray(d)) return [];
      const obj = d as Record<string, unknown>;
      const toRows = (items: unknown, type: string) =>
        Array.isArray(items)
          ? items.map(r => ({ type, ...flattenObject(r as Record<string, unknown>) }))
          : [];
      return [
        ...toRows(obj.results, 'organic'),
        ...toRows(obj.ads, 'ad'),
        ...toRows(obj.amazons_choice, 'amazons_choice')
      ];
    };
    // Mirrors backend flattenReview() / flattenWalmartReview() so the
    // frontend CSV matches the batch-CSV endpoint output exactly.
    const s = (v: unknown): string => (v == null ? '' : String(v));
    const flattenAmazonReview = (
      asin: string,
      r: Record<string, unknown>
    ): Record<string, unknown> => ({
      asin,
      title: s(r.title),
      rating: s(r.stars ?? r.rating),
      date: s(r.date),
      verified: r.verified_purchase ?? r.verified ?? false,
      reviewer: s(r.username ?? r.reviewer_name),
      body: s(r.review ?? r.body),
      helpful_votes: s(r.total_found_helpful ?? r.helpful_votes)
    });
    const flattenWalmartReview = (
      productId: string,
      productName: string,
      r: Record<string, unknown>
    ): Record<string, unknown> => ({
      product_id: productId,
      product_name: productName,
      title: s(r.title),
      text: s(r.text),
      author: s(r.author),
      date: s(r.date_published),
      rating: s(r.rating),
      positive_feedback: s(r.positive_feedback),
      negative_feedback: s(r.negative_feedback),
      verified: Array.isArray(r.badges) && (r.badges as unknown[]).includes('Verified Purchase'),
      badges: Array.isArray(r.badges) ? (r.badges as unknown[]).join(' | ') : ''
    });
    const extractReviewRows = (d: unknown): Record<string, unknown>[] => {
      const products = Array.isArray(d) ? d : [d];
      const rows: Record<string, unknown>[] = [];
      for (const product of products) {
        if (!product || typeof product !== 'object') continue;
        const p = product as Record<string, unknown>;
        const id = s(p.asin ?? p.product_id);
        const productName = s(p.product_name);
        const reviews = Array.isArray(p.reviews) ? (p.reviews as Record<string, unknown>[]) : [];
        if (reviews.length === 0) {
          rows.push(
            platform === AMAZON
              ? flattenAmazonReview(id, {})
              : flattenWalmartReview(id, productName, {})
          );
          continue;
        }
        for (const review of reviews) {
          rows.push(
            platform === AMAZON
              ? flattenAmazonReview(id, review)
              : flattenWalmartReview(id, productName, review)
          );
        }
      }
      return rows;
    };
    const rows = (() => {
      if (tab === AmazonTab.SEARCH) return extractSearchRows(data);
      if (tab === AmazonTab.REVIEWS) return extractReviewRows(data);
      return Array.isArray(data) ? data.map(normalizeRow) : [normalizeRow(data)];
    })();
    if (!rows.length) {
      return;
    }
    const cols = Array.from(new Set(rows.flatMap(r => Object.keys(r))));
    const INVIS_RE = /[\u200B-\u200F\u202A-\u202E\uFEFF]/g;
    const escape = (v: unknown) => {
      const str = String(v ?? '')
        .replace(INVIS_RE, '')
        .replace(/\r?\n/g, ' ');
      return `"${str.replace(/"/g, '""')}"`;
    };
    const header = cols.map(c => `"${c.replace(/"/g, '""')}"`).join(',');
    const body = rows.map(row => cols.map(c => escape(row[c])).join(',')).join('\n');
    downloadCsvBlob(`${header}\n${body}`, 'scraper-results.csv');
  }, [data, platform, tab]);

  // For batch results (arrays), fall back to table view
  const isBatchResult = Array.isArray(data) && (tab === 'product' || tab === 'reviews');

  if (error) {
    return (
      <div className='scraper-results-panel' data-testid='scraper-results'>
        <div className='scraper-error' data-testid='scraper-error'>
          {error}
        </div>
      </div>
    );
  }

  if (loading && !batchJob) {
    return (
      <div className='scraper-results-panel scraper-results-loading' data-testid='scraper-loading'>
        <Spinner size={60} />
      </div>
    );
  }

  if (!data && !batchJob) {
    return (
      <div className='scraper-results-panel scraper-results-empty' data-testid='scraper-empty'>
        <p>Submit a query to see results.</p>
      </div>
    );
  }

  return (
    <div className='scraper-results-panel' data-testid='scraper-results'>
      {batchJob && batchJob.status === 'running' && <BatchProgress job={batchJob} />}
      {batchJob && batchJob.status === 'complete' && (
        <BatchFailureBanner job={batchJob} platform={platform} tab={tab} />
      )}
      {data !== null && (
        <>
          <div className='scraper-results-toolbar' data-testid='scraper-results-toolbar'>
            <div className='scraper-results-title'>
              <span className='results-tab-label'>{capitalize(tab)}</span>
            </div>
            <div className='scraper-download-actions'>
              <Button
                appearance={Appearance.OUTLINED}
                size={Size.SM}
                onClick={handleDownloadCsv}
                leadingElement={<MdOutlineFileDownload />}
              >
                CSV
              </Button>
              <Button
                appearance={Appearance.OUTLINED}
                size={Size.SM}
                onClick={handleDownloadJson}
                leadingElement={<MdOutlineFileDownload />}
              >
                JSON
              </Button>
            </div>
          </div>
          <div className='scraper-results-content'>
            {isBatchResult ? (
              <FallbackTable data={data} />
            ) : (
              <RichContent platform={platform} tab={tab} data={data} />
            )}
          </div>
        </>
      )}
    </div>
  );
};
